import { Inject } from '@odinjs/odin';
import { ButtonAPI, DeckAPI, DeckValue, FetchAPI, FieldAPI, ModalButton, ModalProps,  ModalService } from '@philips-internal/oswald-ui-components';
import Controller, { OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { ClickEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HistoryService, HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { isMobile } from '@philips-internal/oswald-ui-utils';
import { FileItemAliases, FragmentNames } from '../../codegen';
import { YES } from '../../constants/application-constants';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { DocumentProperties } from '../../interfaces/document-properties'
import { MyChartSendingDocumentsType } from '../../constants/my-chart-sending-documents-type';
import UserContext from '../../utils/user-context';
import BaseController from '../BaseController';
import SummaryContext from '../../../src/utils/summary-context';
import PacsLinkGeneratorService from '../../services/pacs-link-generator-service';
import { ModalButtonType } from '@philips-internal/oswald-ui-constants';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { summaryRoute } from '../../constants/summary-constants';

@Controller({ component: FileItemAliases.LAYOUT_FILE_ITEM, fragment: FragmentNames.FILE_ITEM })
export default class FileItemController extends BaseController {

  private decksApiMap: Map<MyChartSendingDocumentsType, DeckAPI> = new Map();

  @Inject(FileItemAliases.DECK_FILE_ITEM)
    deckAPI!: DeckAPI;

  @Inject(FileItemAliases.FETCH_DOCUMENTS)
    fetchAPI!: FetchAPI;

  @Inject(FileItemAliases.BUTTON_VIEW_EXTERNAL_EXAM)
    buttonAPI!: ButtonAPI;

  pacsLinkGeneratorService: PacsLinkGeneratorService = new PacsLinkGeneratorService();

  abortOnClickCard = false;

  @OnMount(FileItemAliases.FETCH_DOCUMENTS)
  setFetchParameters(): void {
    if (HistoryService.getCurrentRoute().includes(summaryRoute)) {
      this.fetchAPI.setParameters({ encounterId: SummaryContext.serviceNumber });
    } else {
      const { patientId } = UserContext;
      this.fetchAPI.setParameters({ encounterId: SummaryContext.serviceNumber, patientId });
    }

  }

  @OnMount({ target: FileItemAliases.FIELD_PACS })
  handlePacsButtonVisibility(event: MountEvent): void {
    const fieldAPI = event.target as FieldAPI;
    const dicomCode = String(fieldAPI.getValue() || '');
    const pacsButton = Array.from(fieldAPI.getChildren()).flat()[1] as unknown as ButtonAPI;

    pacsButton.setVisible(dicomCode.length > 0);
    pacsButton.setClassName(dicomCode);
  }

  @OnClick({ target: FileItemAliases.BUTTON_VIEW_EXTERNAL_EXAM })
  OnClickPacsBtn(event: ClickEvent): void {
    this.abortOnClickCard = true;
    const { patientId } = UserContext;
    const button = event.target as ButtonAPI;
    const dicomCode = button.getClassName();

    const callPacsInChrome = (): void => {
      const windowReference = window.open();
      this.pacsLinkGeneratorService.getPacsState().then(pacsState => {
        if (!pacsState.enabled) {
          alert(MessageService.translate('patient.no-pacs-available'));
          return;
        }

        this.pacsLinkGeneratorService.openPacsInChrome({
          examId: dicomCode?.toString() || '',
          organizationId: '',
          patientId: patientId.toString(),
        }, windowReference)
      });
    }

    const dialogButtons: ModalButton[] = [
      {
        action: () => undefined,
        title: 'patient.cancel',
        type: ModalButtonType.CANCEL,
      },
      {
        action: callPacsInChrome,
        title: 'patient.continue',
        type: ModalButtonType.SUCCESS,
      }
    ];

    const dialogProps: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      dialogDescription: 'patient.pacs.open-new-tab',
      dockButtons: dialogButtons,
      focusOnOpen: true,
      title: 'patient.exam-details',
      uuid: generateUUID(),
    };

    ModalService.openDialog(dialogProps);
  }

  @OnClick(FileItemAliases.DECK_FILE_ITEM)
  handleOnClickDeckFileItem(event: ClickEvent): void {
    if (this.abortOnClickCard) {
      this.abortOnClickCard = false;
      return;
    }
    const fileItem = event.value as DocumentProperties;
    const className = isMobile() ? '' : 'my-chart-sending-documents__dialog-modal';

    ModalService.openDefault({
      className: className,
      contextData: { filePath: fileItem.path },
      fragment: FragmentNames.MY_CHART_VIEW_SENT_DOCUMENT,
      onClose: () => this.markDocumentAsRead(fileItem),
    });
  }

  private markDocumentAsRead(properties: DocumentProperties): void {
    if (properties.read === YES) {
      return;
    }

    const body = {
      documentType: properties.type,
      nrSequence: properties.sequence,
    }

    HttpService.put(APPLICATION_ENDPOINTS.PUT_MC_READ_DOCUMENT, { patientId: UserContext.patientId }, { body })
      .then(() => this.updateItemOnDeck(properties));
  }

  private updateItemOnDeck(properties: DocumentProperties): void {
    const deckAPI = this.decksApiMap.get(properties.type as MyChartSendingDocumentsType);
    if (!deckAPI) {
      return;
    }

    const currentDeckData = deckAPI.getData() as unknown as Array<DocumentProperties>;
    const itemIndex = currentDeckData.findIndex(document => document.sequence === properties.sequence);
    if (itemIndex === -1) {
      return;
    }

    currentDeckData[itemIndex].read = YES;
    deckAPI.setData(currentDeckData as unknown as Array<DeckValue>);
  }

}
